import React, { useEffect, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../../core/components/Loader';
import { getApiStatus } from '../../../core/utils';
import VisualEmbed from './VisualEmbed';
import { fetchVisualReport, GET_VISUAL_REPORT } from '../../analytics/actions';
import { SAFETY_INTERVAL } from '../../analytics/sagas';
import { selectMultiSiteSelection } from '../../site-selection/reducer';
import { selectSiteId } from '../../settings/reducer';

interface ReportConfig {
  embedToken: string;
  embedUrl: string;
  reportId: string;
}

interface Props {
  pageName: string;
  visualId: string;
  title: string;
  selectorFn: (state: any, dashboardType: string) => any;
  dashboardType: string;
}

const MemoizedVisualEmbed = React.memo(VisualEmbed);

export const VisualComponent = (props: Props): JSX.Element => {
  const dispatch = useDispatch();
  const siteId = useSelector(selectSiteId);
  const multiSite = useSelector(selectMultiSiteSelection);
  const intl = useIntl();
  const dashboardType = props?.dashboardType || 'dashboard';

  // Memoize the fetch function to prevent unnecessary re-creation
  const getManagerReport = useCallback(() => {
    dispatch(fetchVisualReport(SAFETY_INTERVAL, 'Manager-Analytics', false, dashboardType));
  }, [dispatch, dashboardType]);

  useEffect(() => {
    getManagerReport();
  }, [getManagerReport, siteId, multiSite]);

  const loader = useSelector((state) => getApiStatus(state, GET_VISUAL_REPORT));
  const reportConfig: ReportConfig = useSelector((state) =>
    props.selectorFn(state, props.dashboardType)
  );

  // Memoize the visual component to prevent unnecessary re-renders
  const visualComponent = useMemo(() => {
    if (!loader && reportConfig) {
      return (
        <MemoizedVisualEmbed
          accessToken={reportConfig.embedToken}
          embedUrl={reportConfig.embedUrl}
          reportId={reportConfig.reportId}
          pageName={props.pageName}
          visualId={props.visualId}
        />
      );
    }
    return (
      <div className="powerbi-loading">
        <Loader />
      </div>
    );
  }, [loader, reportConfig, props.pageName, props.visualId]);

  return (
    <div>
      <section className="container-fluid overview-chart-card">
        <div className="chart-title-section">
          <div className="chart-title">{intl.formatMessage({ id: props.title })}</div>
        </div>
        {visualComponent}
      </section>
    </div>
  );
};

export default VisualComponent;
